/**
 * Shared helper functions for trading recommendations
 * Used across Detail pages and List views
 */

import type { RecommendationCore, RecommendationEarnings, RecommendationSwingtrade, LongTermRecommendation } from '@/models';
import type { TradingRecommendationType } from '@/modelsLocal/trading-recommendation';
import { calculateOpenStatus } from '@/utils/helpers';
import { TRADING_ROUTES } from '@/constants/routes';

type AnyRecommendation = RecommendationCore | RecommendationEarnings | RecommendationSwingtrade | LongTermRecommendation;

/**
 * Get human-readable state label for a trading recommendation
 * Handles OPEN substates for Core/Earnings recommendations
 */
export function getStateLabel(rec: AnyRecommendation): string {
  // For OPEN state of Core/Earnings, calculate substatus
  if (rec.state === 'OPEN') {
    if ('strikePrice' in rec && 'premium' in rec && 'winLossDelta' in rec) {
      const openStatus = calculateOpenStatus(rec.winLossDelta);

      if (openStatus === 'GEHT_DURCH') {
        return 'Geht durch';
      } else if (openStatus === 'WIRD_KNAPP') {
        return 'Wird knapp';
      } else if (openStatus === 'WIRD_EINGEBUCHT') {
        return 'Wird eingebucht';
      }
    }
    return 'Offen';
  }

  // For finished states
  if (rec.state === 'FINISHED_ASSIGNED') {
    return 'Eingebucht';
  }

  if (rec.state === 'FINISHED_UNASSIGNED' || (rec.state as string) === 'FINISHED' || rec.state === 'FINISHED_PROFIT') {
    return 'Erfolgreich beendet';
  }

  if (rec.state === 'FINISHED_LOSS') {
    return 'Beendet';
  }

  return rec.state;
}

/**
 * Get Tailwind CSS classes for state badge color
 * Handles OPEN substates for Core/Earnings recommendations
 */
export function getStateBadgeColor(rec: AnyRecommendation): string {
  // For OPEN state of Core/Earnings, calculate substatus
  if (rec.state === 'OPEN') {
    if ('strikePrice' in rec && 'premium' in rec && 'winLossDelta' in rec) {
      const openStatus = calculateOpenStatus(rec.winLossDelta);

      if (openStatus === 'GEHT_DURCH') {
        return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200';
      } else if (openStatus === 'WIRD_KNAPP') {
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-200';
      } else if (openStatus === 'WIRD_EINGEBUCHT') {
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200';
      }
    }
    return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200';
  }

  // For finished states
  if (rec.state === 'FINISHED_ASSIGNED') {
    return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
  }

  if (rec.state === 'FINISHED_UNASSIGNED' || (rec.state as string) === 'FINISHED' || rec.state === 'FINISHED_PROFIT') {
    return 'bg-lime-100 dark:bg-lime-900/50 text-lime-800 dark:text-lime-200';
  }

  if (rec.state === 'FINISHED_LOSS') {
    return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
  }

  return 'bg-lime-100 dark:bg-lime-900/50 text-lime-800 dark:text-lime-200';
}

/**
 * Get Tailwind CSS classes for status border color (right border)
 * Used for mobile view to indicate status without showing the badge
 */
export function getStatusBorderColor(rec: AnyRecommendation): string {
  // For OPEN state of Core/Earnings, calculate substatus
  if (rec.state === 'OPEN') {
    if ('strikePrice' in rec && 'premium' in rec && 'winLossDelta' in rec) {
      const openStatus = calculateOpenStatus(rec.winLossDelta);

      if (openStatus === 'GEHT_DURCH') {
        return 'border-r-green-500 dark:border-r-green-400';
      } else if (openStatus === 'WIRD_KNAPP') {
        return 'border-r-yellow-500 dark:border-r-yellow-400';
      } else if (openStatus === 'WIRD_EINGEBUCHT') {
        return 'border-r-red-500 dark:border-r-red-400';
      }
    }
    return 'border-r-green-500 dark:border-r-green-400';
  }

  // For finished states
  if (rec.state === 'FINISHED_ASSIGNED') {
    return 'border-r-gray-400 dark:border-r-gray-500';
  }

  if (rec.state === 'FINISHED_UNASSIGNED' || (rec.state as string) === 'FINISHED' || rec.state === 'FINISHED_PROFIT') {
    return 'border-r-lime-400 dark:border-r-lime-200';
  }

  if (rec.state === 'FINISHED_LOSS') {
    return 'border-r-gray-400 dark:border-r-gray-500';
  }

  return 'border-r-lime-400 dark:border-r-lime-200';
}

/**
 * Whether a Swing or Long-Term trade is finished, i.e. part of the history.
 * The history lists query exactly these two states, so this doubles as the
 * "is a history entry" check.
 */
export function isFinishedTrade(trade: RecommendationSwingtrade | LongTermRecommendation): boolean {
  return trade.state === 'FINISHED_PROFIT' || trade.state === 'FINISHED_LOSS';
}

/**
 * Determine the trading recommendation type from the current URL path
 */
export function getTradingRecommendationTypeFromPath(pathname: string): TradingRecommendationType | null {
  if (pathname.includes(TRADING_ROUTES.CORE_RECOMMENDATIONS_BASE)) {
    return 'CORE';
  } else if (pathname.includes(TRADING_ROUTES.EARNINGS_RECOMMENDATIONS_BASE)) {
    return 'EARNINGS';
  } else if (pathname.includes(TRADING_ROUTES.SWING_TRADES_BASE)) {
    return 'SWING';
  } else if (pathname.includes(TRADING_ROUTES.LONG_TERM_TRADES_BASE)) {
    return 'LONGTERM';
  }
  return null;
}

/**
 * Get the back/list link based on recommendation type
 */
export function getListLinkForType(type: TradingRecommendationType | null): string {
  if (type === 'CORE') {
    return TRADING_ROUTES.KERNSTRATEGIE;
  } else if (type === 'EARNINGS') {
    return TRADING_ROUTES.EARNING_TRADES;
  } else if (type === 'SWING') {
    return TRADING_ROUTES.SWING_TRADES;
  } else if (type === 'LONGTERM') {
    return TRADING_ROUTES.LONGTERM_TRADES;
  }
  return '/';
}

/**
 * Get the detail link for a recommendation based on its type
 */
export function getDetailLinkForRecommendation(
  recommendationType: TradingRecommendationType,
  recommendationId: string
): string {
  if (recommendationType === 'CORE') {
    return TRADING_ROUTES.CORE_RECOMMENDATION_DETAIL(recommendationId);
  } else if (recommendationType === 'EARNINGS') {
    return TRADING_ROUTES.EARNINGS_RECOMMENDATION_DETAIL(recommendationId);
  } else if (recommendationType === 'SWING') {
    return TRADING_ROUTES.SWING_TRADE_DETAIL(recommendationId);
  } else if (recommendationType === 'LONGTERM') {
    return TRADING_ROUTES.LONG_TERM_TRADE_DETAIL(recommendationId);
  }
  // This should never happen with proper TypeScript typing
  console.error('Unknown recommendation type:', recommendationType);
  return '/';
}
